const PARALLEL_SPACING = 26;

/**
 * Assigns a `parallelOffset` (in px) to each edge in a group that shares the
 * same target node + target handle. The center edge gets offset 0, edges above
 * get negative values, edges below get positive values.
 *
 * This is used by ParallelEdge to fan out overlapping arrows before they
 * converge on the same connection point.
 *
 * @param {Array} edges
 * @returns {Array} edges with `data.parallelOffset` set
 */
export function assignParallelOffsets(edges) {
  // Group edges by target node + target handle.
  // Self-loops (source === target) are included in the group but always sorted last
  // so they receive the most extreme offset and don't cross regular edges.
  const byTarget = {};
  edges.forEach((edge) => {
    const key = `${edge.target}__${edge.targetHandle ?? ''}`;
    if (!byTarget[key]) byTarget[key] = [];
    byTarget[key].push(edge);
  });

  // Sort each group: regular edges by source weight (asc), self-loops always last.
  const sortedByTarget = {};
  Object.entries(byTarget).forEach(([key, group]) => {
    sortedByTarget[key] = [...group].sort((a, b) => {
      const aSelf = a.source === a.target ? 1 : 0;
      const bSelf = b.source === b.target ? 1 : 0;
      if (aSelf !== bSelf) return bSelf - aSelf;
      const wa = a.data?.sourceWeight ?? 0;
      const wb = b.data?.sourceWeight ?? 0;
      return wa !== wb ? wa - wb : group.indexOf(a) - group.indexOf(b);
    });
  });

  return edges.map((edge) => {
    const key = `${edge.target}__${edge.targetHandle ?? ''}`;
    const sortedGroup = sortedByTarget[key];
    if (!sortedGroup || sortedGroup.length <= 1) {
      return { ...edge, data: { ...edge.data, parallelOffset: 0 } };
    }
    const idx = sortedGroup.findIndex((e) => e.id === edge.id);
    const n = sortedGroup.length;
    const offset = ((n - 1) / 2 - idx) * PARALLEL_SPACING;
    return { ...edge, data: { ...edge.data, parallelOffset: offset } };
  });
}

/**
 * Converts a Drupal workflow config object to React Flow nodes and edges.
 *
 * Drupal format:
 * {
 *   id, label, type,
 *   states: { stateId: { label, published, weight } },
 *   transitions: { transitionId: { label, from: [...], to, weight, allowedRoles: [...] } }
 * }
 *
 * React Flow nodes: array of { id, type, position, data }
 * React Flow edges: array of { id, source, target, type, markerEnd, data }
 */

import { MarkerType } from '@xyflow/react';

const LAYOUT_COLUMN_WIDTH = 220;
const LAYOUT_ROW_HEIGHT = 130;

/**
 * Converts a Drupal workflow config to React Flow nodes and edges.
 *
 * Layout: states are arranged in columns by weight order. States sharing a
 * weight bucket are stacked vertically. Positions are auto-computed and can
 * be overridden by a stored layout in the config's `_layout` key.
 *
 * @param {Object} drupalConfig - The workflow config returned by the API.
 * @returns {{ nodes: Array, edges: Array }}
 */
export function toFlow(drupalConfig) {
  const { states = {}, transitions = {}, _layout = {} } = drupalConfig;

  // Sort states by weight to determine column order.
  const sortedStateIds = Object.keys(states).sort(
    (a, b) => (states[a].weight ?? 0) - (states[b].weight ?? 0),
  );

  // Assign positions column by column; use stored layout if available.
  const nodes = sortedStateIds.map((stateId, index) => {
    const state = states[stateId];
    const storedPos = _layout[stateId];
    return {
      id: stateId,
      type: 'state',
      position: storedPos ?? {
        x: index * LAYOUT_COLUMN_WIDTH + 40,
        y: 100,
      },
      data: {
        stateId,
        label: state.label,
        published: state.published ?? false,
        weight: state.weight ?? 0,
      },
    };
  });

  // Each Drupal transition can have multiple `from` states, which React Flow
  // models as one edge per (from, to) pair. We keep the transitionId on all
  // edges that share the same logical transition.
  const edges = [];
  Object.entries(transitions).forEach(([transitionId, transition]) => {
    const froms = Array.isArray(transition.from) ? transition.from : [transition.from];
    froms.forEach((fromStateId, idx) => {
      const isSelfLoop = fromStateId === transition.to;

      // Choose source/target handles based on weight comparison so arrows flow
      // in a consistent direction: forward (↓ bottom→left), backward (↑ top→right),
      // same-weight (→ right→left).
      let sourceHandle, targetHandle;
      if (isSelfLoop) {
        // Self-loops use the same ParallelEdge renderer as regular transitions.
        // bottom→left gives a clean angular path below-left of the node.
        sourceHandle = 'source-bottom';
        targetHandle = 'target-left';
      } else {
        const sourceWeight = states[fromStateId]?.weight ?? 0;
        const targetWeight = states[transition.to]?.weight ?? 0;
        if (sourceWeight < targetWeight) {
          sourceHandle = 'source-bottom';
          targetHandle = 'target-left';
        } else if (sourceWeight > targetWeight) {
          sourceHandle = 'source-top';
          targetHandle = 'target-right';
        } else {
          sourceHandle = 'source-right';
          targetHandle = 'target-left';
        }
      }

      edges.push({
        id: `${transitionId}__from__${fromStateId}`,
        source: fromStateId,
        target: transition.to,
        sourceHandle,
        targetHandle,
        type: 'parallel',
        markerEnd: { type: MarkerType.ArrowClosed },
        label: transition.label,
        data: {
          transitionId,
          label: transition.label,
          weight: transition.weight ?? 0,
          allowedRoles: transition.allowedRoles ?? [],
          sourceWeight: states[fromStateId]?.weight ?? 0,
          fromIndex: idx,
          fromCount: froms.length,
          isSelfLoop,
        },
      });
    });
  });

  return { nodes, edges };
}

/**
 * Recalculates sourceHandle / targetHandle for every edge based on the current
 * node weights, and updates data.sourceWeight accordingly.
 *
 * Must be called whenever a node's weight changes so that arrows continue to
 * exit/enter from the correct side (bottom→left for forward, top→right for
 * backward, right→left for same-weight).
 *
 * @param {Array} edges  - Current React Flow edges.
 * @param {Array} nodes  - Current React Flow nodes (with updated weights).
 * @returns {Array} edges with updated sourceHandle / targetHandle / data.sourceWeight
 */
export function recalculateEdgeHandles(edges, nodes) {
  const weightById = {};
  nodes.forEach((n) => {
    weightById[n.id] = n.data?.weight ?? 0;
  });

  return edges.map((edge) => {
    if (edge.data?.isSelfLoop) {
      return {
        ...edge,
        data: { ...edge.data, sourceWeight: weightById[edge.source] ?? 0 },
      };
    }
    const sourceWeight = weightById[edge.source] ?? 0;
    const targetWeight = weightById[edge.target] ?? 0;
    let sourceHandle, targetHandle;
    if (sourceWeight < targetWeight) {
      sourceHandle = 'source-bottom';
      targetHandle = 'target-left';
    } else if (sourceWeight > targetWeight) {
      sourceHandle = 'source-top';
      targetHandle = 'target-right';
    } else {
      sourceHandle = 'source-right';
      targetHandle = 'target-left';
    }
    return {
      ...edge,
      sourceHandle,
      targetHandle,
      data: { ...edge.data, sourceWeight },
    };
  });
}

/**
 * Converts React Flow nodes and edges back to Drupal workflow config format.
 *
 * Multiple edges with the same transitionId (multi-from transitions) are merged
 * into a single transition with a `from` array.
 *
 * @param {Array} nodes - React Flow nodes.
 * @param {Array} edges - React Flow edges.
 * @param {Object} meta - Metadata: { id, label, type } from the original config.
 * @returns {Object} Drupal-format workflow config ready to POST.
 */
export function toDrupal(nodes, edges, meta) {
  const states = {};
  nodes.forEach((node) => {
    states[node.id] = {
      label: node.data.label,
      published: node.data.published ?? false,
      weight: node.data.weight ?? 0,
    };
  });

  // Merge edges that share the same transitionId.
  const transitionsMap = {};
  edges.forEach((edge) => {
    const { transitionId, label, weight, allowedRoles } = edge.data ?? {};
    if (!transitionId) return;

    if (!transitionsMap[transitionId]) {
      transitionsMap[transitionId] = {
        label: label ?? transitionId,
        from: [],
        to: edge.target,
        weight: weight ?? 0,
        allowedRoles: allowedRoles ?? [],
      };
    }
    if (!transitionsMap[transitionId].from.includes(edge.source)) {
      transitionsMap[transitionId].from.push(edge.source);
    }
  });

  // Store node positions for round-trip layout preservation.
  const _layout = {};
  nodes.forEach((node) => {
    _layout[node.id] = node.position;
  });

  return {
    id: meta.id,
    label: meta.label,
    type: meta.type,
    states,
    transitions: transitionsMap,
    _layout,
  };
}
